import { Link } from 'react-router-dom';
import ChartDataCallout, { ChartDataCalloutList } from '../ui/ChartDataCallout';
import { buildValuationNarrative } from '../../utils/valuationNarrative';

export const EVIDENCE_META = {
  evidenced: {
    label: 'Evidenced valuation',
    blurb: 'Backed by recent same-type sales in this postcode — our most defensible figure.',
    colour: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  },
  inferred: {
    label: 'Inferred valuation',
    blurb: 'Inferred from adjusted comparables — wider area or cross-type extrapolation.',
    colour: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  },
  modelled: {
    label: 'Modelled estimate',
    blurb: 'Insufficient comparable evidence — statistically modelled, not market-justified alone.',
    colour: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  },
  none: {
    label: 'Limited local data',
    blurb: 'Few directly comparable recent sales in this district.',
    colour: 'text-slate-400 bg-slate-800 border-slate-700',
  },
};

const TIER_LABEL = {
  sector_tight: 'postcode sector · same beds/size',
  sector_wide: 'postcode sector · wider size range',
  district_wide: 'postcode district · loose filters',
  district_wide_raw: 'postcode district · sales without EPC match',
  nearby_district: 'neighbouring district · same type',
  nearby_district_raw: 'neighbouring district · wider sales pool',
  cross_type_psqm: 'district £/sqm · different property types',
  district_mean: 'district average · no direct comparables',
  none: 'no matches',
};

function fmtGBP(n) {
  if (n == null) return '—';
  return `£${Math.round(n).toLocaleString('en-GB')}`;
}

export default function ComparablesPanel({ avm, loading, postcode, guidePrice, areaStats }) {
  if (loading) {
    return <p className="text-slate-500 text-sm">Calculating from comparable sales…</p>;
  }
  if (!avm) {
    return <p className="text-slate-500 text-sm">Could not load comparable sales for this address.</p>;
  }

  const evidence = EVIDENCE_META[avm.evidence_tier] || EVIDENCE_META.none;
  const tier = TIER_LABEL[avm.tier_used] || avm.tier_used;
  const district = postcode?.split(' ')[0];
  const story = buildValuationNarrative({ avm, areaStats, postcode });

  return (
    <div className="space-y-4">
      {story && (
        <ChartDataCallout tone={story.tone} title={story.title}>
          <ol className="space-y-3 mt-1">
            {story.steps.map((step, i) => (
              <li key={step.label} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
                    {step.label}
                  </p>
                  <p className="text-slate-300">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </ChartDataCallout>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-slate-400 text-sm">
            {avm.comparable_count > 0
              ? `Based on ${avm.comparable_count} comparable sold ${avm.comparable_count === 1 ? 'home' : 'homes'} · ${tier}`
              : `Modelled from district sales · ${tier}`}
          </p>
        </div>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full border whitespace-nowrap ${evidence.colour}`}>
          {evidence.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <ValueCell label="Low" value={avm.estimate_low} />
        <ValueCell label="Mid" value={avm.estimate_mid} highlight />
        <ValueCell label="High" value={avm.estimate_high} />
      </div>

      <ChartDataCallout tone={avm.evidence_tier === 'evidenced' ? 'emerald' : 'amber'} title="What this number means">
        {evidence.blurb}
        <ChartDataCalloutList
          items={[
            'Evidenced — recent same-type sales in the same postcode area.',
            'Inferred — widened search or adjusted comparables.',
            'Modelled — statistical fallback when direct comps are thin.',
          ]}
        />
      </ChartDataCallout>

      {avm.search_widened_to && (
        <ChartDataCallout tone="amber" title="Search widened beyond this district">
          Few closely matched sales in <strong className="text-slate-200">{district || 'this district'}</strong>.
          Estimate uses sales from <strong className="text-slate-200">{avm.search_widened_to}</strong>.
        </ChartDataCallout>
      )}

      {avm.sparse_explanation?.summary && (
        <ChartDataCallout tone="amber" title="Limited local evidence">
          {avm.sparse_explanation.summary}
          {avm.sparse_explanation.hints?.length > 0 && (
            <ChartDataCalloutList items={avm.sparse_explanation.hints} />
          )}
        </ChartDataCallout>
      )}

      {guidePrice && avm.estimate_mid && (
        <p className="text-sm text-slate-400">
          Reference price {fmtGBP(guidePrice)} vs mid estimate {fmtGBP(avm.estimate_mid)} (
          {(((guidePrice - avm.estimate_mid) / avm.estimate_mid) * 100).toFixed(1)}%)
        </p>
      )}

      {avm.comparables?.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Comparable sales used</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-xs border-b border-slate-800">
                  <th className="pb-2 pr-3">Address</th>
                  <th className="pb-2 pr-3">Date</th>
                  <th className="pb-2 pr-3">Price</th>
                  <th className="pb-2">Match</th>
                </tr>
              </thead>
              <tbody>
                {avm.comparables.slice(0, 12).map((c, i) => (
                  <tr key={i} className="border-b border-slate-800/50">
                    <td className="py-2 pr-3 text-slate-300">{c.address || c.postcode}</td>
                    <td className="py-2 pr-3 text-slate-400">{c.sale_date?.slice(0, 7) || '—'}</td>
                    <td className="py-2 pr-3 text-emerald-400 font-medium">{fmtGBP(c.sale_price)}</td>
                    <td className="py-2 text-slate-500 text-xs">{c.match_note || c.property_type || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {postcode && (
        <Link
          to={`/neighbourhood/${encodeURIComponent(postcode)}`}
          className="text-sm text-blue-400 hover:text-blue-300 inline-block"
        >
          Open full neighbourhood map & overlays →
        </Link>
      )}
    </div>
  );
}

function ValueCell({ label, value, highlight }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-800 bg-slate-800/40'}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold mt-1 ${highlight ? 'text-emerald-400' : 'text-white'}`}>
        {fmtGBP(value)}
      </p>
    </div>
  );
}
